import { EventDrivenStore } from '@core/store/eventDrivenStore';
import constants from '@core/utils/constants';
import { getMatchesByDate } from './list/matchDataUtils';
import { ref } from 'vue';

class Store extends EventDrivenStore {
  constructor() {
    const defaultState = {
      list_all: {
        list: [],
        error: null,
        loading: true
      },
      live: {
        list: [],
        error: null,
        loading: true
      },
      pronostics: {
        list: [],
        error: null,
        loading: true
      },
      lastRequestTimestamps: {}
    };

    super('match', defaultState, {
      useLocalStorage: false
    });

    this.defaultState = defaultState;
    this.formattedMatchesByDate = ref([]);
    // identifiant de génération pour éviter les calculs obsolètes
    this.formatGeneration = 0;
  }

  async setupEvents() {
    this.on(constants.EVENTS.MATCH.LIST_ALL.REFRESHING, () => {
      this.state.list_all.loading = true;
      this.state.list_all.error = null;
    });

    this.on(constants.EVENTS.MATCH.LIST_ALL.REFRESHED, (data) => {
      console.log('🚀 ~ Store ~ setupEvents ~ data:', data);
      if (Array.isArray(data.data)) {
        this.state.list_all.list = [
          ...data.data,
          ...this.state.list_all.list.filter(
            (el) => !data.data.find((elSaved) => elSaved.id == el.id)
          )
        ];
      }
      this.formatMatchesByDate();
      this.state.list_all.loading = false;
      this.state.list_all.error = Array.isArray(data.data)
        ? null
        : 'Mauvais format de donnée';
    });

    this.on(constants.EVENTS.MATCH.LIST_ALL.REFRESH_ERROR, (error) => {
      this.state.list_all.loading = false;
      this.state.list_all.error = error?.message || error;
      this.state.list_all.list = [];
      this.formatMatchesByDate();
    });

    this.on(constants.EVENTS.MATCH.LIVE.REFRESHING, () => {
      this.state.live.loading = true;
      this.state.live.error = null;
    });

    this.on(constants.EVENTS.MATCH.LIVE.REFRESHED, (data) => {
      this.state.live.list = Array.isArray(data) ? data : [];
      this.state.live.loading = false;
      this.state.live.error = Array.isArray(data)
        ? null
        : 'Mauvais format de donnée';
    });

    this.on(constants.EVENTS.MATCH.LIVE.REFRESH_ERROR, (error) => {
      this.state.live.loading = false;
      this.state.live.error = error?.message || error;
      this.state.live.list = [];
    });

    this.on(constants.EVENTS.MATCH.PRONOSTICS.REFRESHING, () => {
      !this.state.pronostics &&
        (this.state.pronostics = { ...this.defaultState.pronostics });
      this.state.pronostics.loading = true;
      this.state.pronostics.error = null;
    });

    this.on(constants.EVENTS.MATCH.PRONOSTICS.REFRESHED, (data) => {
      this.state.pronostics.list = Array.isArray(data) ? data : [];
      this.state.pronostics.loading = false;
      this.state.pronostics.error = Array.isArray(data)
        ? null
        : 'Mauvais format de donnée';
    });

    this.on(constants.EVENTS.MATCH.PRONOSTICS.REFRESH_ERROR, (error) => {
      this.state.pronostics.loading = false;
      this.state.pronostics.error = error?.message || error;
      this.state.pronostics.list = [];
    });
  }

  getLastRequestTimestamp = (key) =>
    this.state.lastRequestTimestamps?.[key] || 0;
  setLastRequestTimestamp = (key) => {
    !this.state.lastRequestTimestamps &&
      (this.state.lastRequestTimestamps = {});
    this.state.lastRequestTimestamps[key] = Date.now();
  };

  formatMatchesByDate() {
    // incrémenter la génération pour annuler les calculs précédents
    this.formatGeneration++;
    const currentGeneration = this.formatGeneration;

    console.time('[PERF] allMatchesData computed');
    const dates = constants.MATCH.ALL_DATES;
    const matchesList = this.state?.list_all?.list || [];

    // récupérer les IDs déjà présents pour éviter de les recalculer
    const existingIds = new Set(
      this.formattedMatchesByDate.value
        .filter((d) => !d.isLoadingMatches)
        .map((d) => d.id)
    );
    console.log('🚀 ~ Store ~ formatMatchesByDate ~ existingIds:', existingIds);

    // trouver les dates qui n'ont pas encore été calculées
    const datesToProcess = dates.filter((d) => !existingIds.has(d.id));

    if (datesToProcess.length === 0) {
      console.log('[PERF] no new dates to process');
      console.timeEnd('[PERF] allMatchesData computed');
      return;
    }

    console.log(
      `[PERF] processing ${datesToProcess.length} new dates out of ${dates.length} total`
    );

    // traiter par chunks de 5 dates à la fois pour ne pas bloquer le thread
    const CHUNK_SIZE = 5;

    const processChunk = (startIndex) => {
      // vérifier si ce calcul est encore valide
      if (currentGeneration !== this.formatGeneration) {
        console.log('[PERF] format generation obsolete, skipping');
        return;
      }

      const endIndex = Math.min(startIndex + CHUNK_SIZE, datesToProcess.length);

      // traiter ce chunk et fusionner avec les existants en gardant l'ordre
      const newFormatted = datesToProcess.slice(0, endIndex).map((dateData) => {
        console.time(`[PERF] getMatchesByDate for ${dateData.id}`);
        const parsedMatchData = getMatchesByDate(dateData.id, matchesList);
        console.timeEnd(`[PERF] getMatchesByDate for ${dateData.id}`);
        return {
          ...dateData,
          dayMatches: parsedMatchData.matches,
          isLoadingMatches: parsedMatchData.loading
        };
      });

      // fusionner en gardant l'ordre chronologique correct
      const allDates = new Map(
        this.formattedMatchesByDate.value.map((d) => [d.id, d])
      );
      newFormatted.forEach((d) => allDates.set(d.id, d));

      // convertir en tableau trié selon l'ordre original de constants.MATCH.ALL_DATES
      const sortedResult = dates.map((d) => allDates.get(d.id)).filter(Boolean);

      this.formattedMatchesByDate.value = sortedResult;

      if (endIndex < datesToProcess.length) {
        // continuer avec le prochain chunk quand le navigateur est idle
        requestIdleCallback(() => processChunk(endIndex));
      } else {
        console.timeEnd('[PERF] allMatchesData computed');
      }
    };

    // démarrer le traitement
    processChunk(0);
  }
}

export default Store;
// new Store();
